import { useCallback, useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Award, RefreshCw, Search, MapPin, Ban, CheckCircle2, Trophy, Download } from "lucide-react";

/**
 * Admin/Editor Redemption Log — every voucher that has been redeemed,
 * with when + where breadcrumbs plus a search filter.
 */
export default function RedemptionLogCard({ scopeSlug = null }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [allRewards, setAllRewards] = useState([]);
  const [issuedCount, setIssuedCount] = useState(0);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const suffix = scopeSlug ? `?scope_slug=${encodeURIComponent(scopeSlug)}` : "";
      const [redRes, allRes] = await Promise.all([
        api.get(`/admin/rewards/redemptions${suffix}`),
        api.get(`/admin/rewards/all${suffix}`),
      ]);
      setRows(Array.isArray(redRes.data) ? redRes.data : []);
      const all = Array.isArray(allRes.data) ? allRes.data : [];
      setAllRewards(all);
      setIssuedCount(all.length);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [scopeSlug]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      [r.code, r.user_name, r.user_email, r.season_name, r.sponsor_name, r.redemption_location, r.prize_display]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t))
    );
  }, [rows, q]);

  const redemptionRate =
    issuedCount > 0 ? Math.round((rows.length / issuedCount) * 100) : 0;

  // iter140 — Expired voucher count. A voucher is "expired" when it has
  // an `expires_at` in the past AND has NOT been redeemed. Sponsor vouchers
  // stay claimable at the pub until either the winner redeems them or the
  // sponsor's cutoff date passes; this stat tells the admin how many are
  // now unreachable (money left on the table).
  const expiredCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);  // "YYYY-MM-DD"
    return allRewards.filter(
      (r) => !r.redeemed_at && r.expires_at && r.expires_at < today,
    ).length;
  }, [allRewards]);

  // ---- CSV export -------------------------------------------------------
  // Exports the currently-filtered rows so the search box also acts as a
  // "narrow the export" tool. If no filter, exports everything redeemed.
  const csvEscape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const header = [
      "redeemed_at",
      "player_name",
      "player_email",
      "rank",
      "season",
      "prize",
      "sponsor",
      "venue",
      "staff_note",
      "code",
      "issued_at",
      "expires_at",
    ];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push(
        [
          r.redeemed_at,
          r.user_name,
          r.user_email,
          r.rank,
          r.season_name,
          r.prize_display,
          r.sponsor_name,
          r.redemption_location,
          r.redemption_staff_note,
          r.code,
          r.issued_at,
          r.expires_at,
        ]
          .map(csvEscape)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dugsy-redemptions-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} redemption${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <section
      className="mt-12 border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="admin-redemption-log-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Award className="h-4 w-4 text-zinc-500" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">
              Voucher summary
            </h2>
          </div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Every voucher claimed by a winner, with when + where it happened.
            Filter by code, name, sponsor, or venue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
            data-testid="admin-redemption-export-csv"
            title={
              filtered.length === 0
                ? "No redemptions to export"
                : `Export ${filtered.length} row${filtered.length === 1 ? "" : "s"} as CSV`
            }
            className="h-9 rounded-sm border-zinc-300 text-xs"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="admin-redemption-refresh"
            className="h-9 rounded-sm border-zinc-300 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="border border-zinc-200 rounded-sm p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Issued</p>
          <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-zinc-950" data-testid="admin-red-total-issued">
            {issuedCount}
          </p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Redeemed</p>
          <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-emerald-700" data-testid="admin-red-total-redeemed">
            {rows.length}
          </p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Outstanding</p>
          <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-amber-700" data-testid="admin-red-total-outstanding">
            {Math.max(0, issuedCount - rows.length - expiredCount)}
          </p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4" data-testid="admin-red-expired-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Expired vouchers</p>
          <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-rose-700" data-testid="admin-red-total-expired">
            {expiredCount}
          </p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Redemption rate</p>
          <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-zinc-950" data-testid="admin-red-rate">
            {redemptionRate}%
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mt-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search code, player, sponsor or venue…"
          data-testid="admin-redemption-search"
          className="pl-9 h-10 rounded-sm border-zinc-300 text-sm"
        />
      </div>

      {/* Rows */}
      {loading ? (
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <div
          className="mt-6 border border-dashed border-zinc-300 rounded-sm p-8 text-center"
          data-testid="admin-redemption-empty"
        >
          <Trophy className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 text-sm text-zinc-600">
            {rows.length === 0
              ? "No vouchers have been redeemed yet."
              : "No redemptions match that filter."}
          </p>
        </div>
      ) : (
        <div className="mt-6 border border-zinc-200 rounded-sm overflow-hidden" data-testid="admin-redemption-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Redeemed at</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Player</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Prize</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Venue</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Code</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.code}
                  className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
                  data-testid={`admin-redemption-row-${r.code}`}
                >
                  <td className="px-4 py-3 tabular-nums text-zinc-700">
                    <div className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      {new Date(r.redeemed_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-zinc-950">{r.user_name || "—"}</p>
                    {r.user_email ? <p className="text-[11px] text-zinc-500">{r.user_email}</p> : null}
                    {r.season_name ? (
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mt-0.5">
                        Rank #{r.rank} · {r.season_name}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-zinc-950">{r.prize_display}</p>
                    {r.sponsor_name ? <p className="text-[11px] text-zinc-500">{r.sponsor_name}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">
                    {r.redemption_location ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                        {r.redemption_location}
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-xs">not recorded</span>
                    )}
                    {r.redemption_staff_note ? (
                      <p className="text-[11px] text-zinc-500 mt-1 italic">&ldquo;{r.redemption_staff_note}&rdquo;</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] tracking-widest text-zinc-700">
                    {r.code}
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.14em] bg-rose-600 text-white"
                      data-testid={`admin-red-used-pill-${r.code}`}
                    >
                      <Ban className="h-2.5 w-2.5" /> Used
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
